import { motion } from 'framer-motion';
import { 
  RocketLaunchIcon, 
  CpuChipIcon, 
  GlobeAltIcon 
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';

const features = [
  {
    title: 'Enterprise Focus',
    description: "We specialize in building robust, scalable solutions that meet the complex needs of modern enterprises.",
    icon: RocketLaunchIcon,
  },
  {
    title: 'Innovation Driven',
    description: 'Leveraging cutting-edge technologies to deliver intelligent, future-proof solutions.',
    icon: CpuChipIcon,
  },
  {
    title: 'Global Scale',
    description: 'Our solutions are designed to scale globally, supporting businesses of all sizes.',
    icon: GlobeAltIcon,
  },
];

const About = () => {
  const stats = [
    { label: 'Year Started', value: '2025' },
    { label: 'Projects Ongoing', value: '3' },
    { label: 'Stage', value: 'Initial' },
    { label: 'Team Members', value: '4' },
  ];

  return (
    <>
      <Helmet>
        <title>About Nayrix | Enterprise Software Experts</title>
        <meta name="description" content="Learn about Nayrix, our mission, vision, and expertise in enterprise software solutions." />
        <meta property="og:title" content="About Nayrix | Enterprise Software Experts" />
        <meta property="og:description" content="Learn about Nayrix, our mission, vision, and expertise in enterprise software solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nayrix.com/about" />
        <link rel="canonical" href="https://nayrix.com/about" />
      </Helmet>
      <section id="about" className="py-24 dark:bg-dark-bg/50 bg-light-bg/50">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 dark:text-dark-text text-light-text">
              About Nayrix
            </h2>
            <p className="text-xl dark:text-dark-text-secondary text-light-text-secondary max-w-3xl mx-auto">
              We are a team of enterprise software specialists dedicated to building powerful, scalable solutions that drive business growth.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="p-8 rounded-2xl dark:bg-dark-bg bg-light-bg dark:border-dark-border border-light-border border">
                <h3 className="text-2xl font-semibold mb-4 dark:text-dark-text text-light-text">Our Mission</h3>
                <p className="dark:text-dark-text-secondary text-light-text-secondary mb-6">
                  To empower enterprises with innovative software solutions that streamline operations, enhance productivity, and drive sustainable growth.
                </p>
                <h3 className="text-2xl font-semibold mb-4 dark:text-dark-text text-light-text">Our Vision</h3>
                <p className="dark:text-dark-text-secondary text-light-text-secondary">
                  To be the trusted partner for enterprises worldwide, delivering cutting-edge software solutions that transform how businesses operate in the digital age.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 gap-6"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="rounded-xl bg-white/80 dark:bg-dark-bg/70 backdrop-blur-md border border-neutral-200 dark:border-dark-border shadow-sm flex flex-col items-center justify-center py-6 px-2"
                  style={{ minHeight: '70px' }}
                >
                  <div className="text-2xl md:text-3xl font-display font-semibold text-primary-700 dark:text-primary-300 mb-1 tracking-tight">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm font-medium text-neutral-700 dark:text-neutral-300 tracking-wide uppercase">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-2xl dark:bg-dark-bg bg-light-bg dark:border-dark-border border-light-border border"
              >
                <feature.icon className="w-12 h-12 dark:text-dark-accent text-light-accent mb-4" />
                <h3 className="text-xl font-semibold mb-2 dark:text-dark-text text-light-text">
                  {feature.title}
                </h3>
                <p className="dark:text-dark-text-secondary text-light-text-secondary">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <a 
              href="#contact" 
              className="px-8 py-3 rounded-full dark:bg-dark-accent hover:bg-dark-accent-hover bg-light-accent hover:bg-light-accent-hover text-white transition-colors duration-300"
            >
              Let's Work Together
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;